import { deployedBuild, dispatchBuild, isBuildImage, manifestBuildImages } from '../core/imageBuild'
import type { BuildReadout } from '../features/instances/BuildPanel'
import { get } from '../data/client'
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useBids, useCatalog, useCapacityEnvelope, useChain, useDeployment, useDeployments, useEscrow, useLeases, useMyIp, useProvider, usePaymentsActivity, usePaymentsSummary, useProviders } from '../data/queries'
import { ingressState } from '../core/access'
import { useSignedTx } from '../data/mutations'
import { useAdminUsers, useOperatorDeployments, useOperatorFleet, useOperatorLogs, useOperatorNodeports, useOperatorNodes, useOperatorPods, useOperatorProviders, useOperatorServices } from '../data/queries'
import { useOperatorAction } from '../data/mutations'
import { useAdminRoleChange } from '../data/mutations'
import { useProviderPower } from '../data/mutations'
import { endpoints } from '../data/endpoints'
import type { Account, CatalogHostDto, ChainInfo, IdentityDto } from '../data/types'
import { CatalogPage } from '../features/catalog/CatalogPage'
import { ConfigurePage } from '../features/configure/ConfigurePage'
import type { MyIpState } from '../features/configure/PortAccess'
import { InstancesPage, viewFromSearch } from '../features/instances/InstancesPage'
import { buildInstanceList, type InstanceRowModel } from '../data/instances'
import { buildBillingModel, type BillingViewModel } from '../data/billing'
import type { BillingSource } from '../features/billing/BillingPage'
import { buildInstanceDetail, parseHeight } from '../data/instanceDetail'
import { browserStorage, readDeploymentLabels } from '../core/deploymentLabels'
import { POLL_NO_TOKEN } from '../features/instances/statusPoll'
import { OverviewPage } from '../features/overview/OverviewPage'
import { SettingsPage } from '../features/settings/SettingsPage'
import { ActivityPage } from '../features/activity/ActivityPage'
import { OperatorPage, type OperatorSection } from '../features/operator/OperatorPage'
import type { Connection } from '../core/wallet'
import { DEFAULT_GUIDED_DRAFT, guidedDraftToSdl, isUntouchedDraft, normalizeGuidedDraft, readStoredDraft, rentalDraftToSdl, requiresAllowlist, type GuidedDraft } from '../core/guided'
import { providerEnforcesAllowlist as hostEnforcesAllowlist } from '../core/allowFrom'
import { discardStoredDraft, readDraftOrigin, resumePathFor } from '../core/draftOrigin'
import { hostDisplayName } from '../core/hostName'
import { RENTAL_PATHS, isHostProduct, resolveMode, type RentalPath } from '../core/rental'
import { formatResources } from '../core/sizing'
import { emptyOffersCopy, freshOffers, offerPriceForMode, offersForMode, rentalErrorCopy, unavailablePaths, useRentalCapability, useRentalOffers } from '../data/rental'
import { DirectRentalPage } from '../features/rentalpath/DirectRentalPage'
import { clusterPublicHost } from '../features/provider/LeaseStatusView'
import { quoteRequestFor, type GroupPriceCeiling } from '../features/rentalpath/prepareDirectRental'
import { lowestObservedMicrosPerHour, usdDdtPerHour } from '../core/pricing'
import { RENTAL_PATH_COPY } from '../core/rental'
import type { QuoteWire } from '../core/protocol/directRental'
import { clearRentalDraft, loadRentalDraft, saveRentalDraft, type RentalDraft } from '../core/rentalDraft'
import type { RentalCapacityDto, RentalErrorDto, RentalHostOfferDto, RentalQuoteDto } from '../data/types'
import { RentStartPage } from '../features/rentalpath/RentStartPage'
import { leaseForDseq, leaseIdOf, observedReferencePrices, pendingDeployments, toLeaseList, toOfferList } from '../data/adapters'
import type { BidsDto, EscrowDto, LeaseListDto } from '../data/types'
import type { PaymentsPanelProps } from '../features/billing/PaymentsPanel'
import { paymentDestination, type ActivityRowDto, type FundingIntentDto } from '../data/payments'
import type { QueryLike } from '../features/shared/DataGuard'
import { PageHeader } from '../features/shared/PageHeader'
import { findGuide } from '../features/docs/guides'
import { clearPendingSdl, deployedSdl, rememberDeployedSdl, rememberPendingSdl } from '../core/deployedSdl'
import type { UpdatePlan } from '../core/instanceUpdate'
import { s3EndpointFromUris, type S3InstanceCandidate } from '../core/s3Connection'
import { s3InstanceCandidates } from '../data/s3Instances'
import type { S3InstancesState } from '../features/configure/S3Connection'
import { EmptyState } from '../ui/Feedback'
import { Button } from '../ui/Button'
import { rentalPathHref } from '../features/catalog/hostLabels'
import { plaintextExposure, unacknowledgedPlaintext } from '../core/popularImages'

// The oldest rental contract this console quotes against; see unavailablePaths.
const MIN_RENTAL_CAPABILITY_VERSION = 1

// A remembered offer carries no capacity snapshot; nothing reads one from it.
const NO_CAPACITY: RentalCapacityDto = { cpu_millicores: '0', memory_bytes: '0', storage_bytes: '0', persistent_storage_bytes: '0', gpu_units: '0' }

function mapQuery<T, U>(query: QueryLike<T>, map: (value: T) => U): QueryLike<U> {
  return {
    ...query,
    data: query.data === undefined ? undefined : map(query.data),
    refetch: async () => {
      const next = await query.refetch()
      return next === undefined ? undefined : map(next)
    },
  }
}

const SdlEditor = lazy(() => import('../features/sdl/SdlEditor').then((module) => ({ default: module.SdlEditor })))
const BillingPage = lazy(() => import('../features/billing/BillingPage').then((module) => ({ default: module.BillingPage })))
const OffersPage = lazy(() => import('../features/offers/OffersPage').then((module) => ({ default: module.OffersPage })))
const ReviewPage = lazy(() => import('../features/review/ReviewPage').then((module) => ({ default: module.ReviewPage })))
const InstanceDetailPage = lazy(() => import('../features/instances/InstanceDetailPage').then((module) => ({ default: module.InstanceDetailPage })))
const DocsIndexPage = lazy(() => import('../features/docs/DocsPage').then((module) => ({ default: module.DocsIndexPage })))
const GuidePage = lazy(() => import('../features/docs/DocsPage').then((module) => ({ default: module.GuidePage })))

export function OverviewRoute({ account, walletConnected = Boolean(account), accountLoading = false, accountError, onRetryAccount, onConnect, onOpenWallet, onNavigate }: { account: Account | null; walletConnected?: boolean; accountLoading?: boolean; accountError?: unknown; onRetryAccount?: () => void; onConnect?: (() => void) | undefined; onOpenWallet?: (() => void) | undefined; onNavigate?: ((path: string) => void) | undefined }) {
  const leases = mapQuery<LeaseListDto, ReturnType<typeof toLeaseList>>(useLeases(account?.address), toLeaseList)
  const escrow = useEscrow(account?.address)
  const payments = usePaymentsSummary(walletConnected)
  const chainHeight = parseHeight(useChain().data?.height)
  return <OverviewPage account={account} walletConnected={walletConnected} accountLoading={accountLoading} accountError={accountError} onRetryAccount={onRetryAccount} leases={leases} escrow={escrow} chainHeight={chainHeight} payments={payments} onConnect={onConnect} onOpenWallet={onOpenWallet} onNavigate={onNavigate} />
}

export function CatalogRoute({ onNavigate }: { onNavigate?: ((path: string) => void) | undefined }) {
  const query = useCatalog(new URLSearchParams(window.location.search))
  const chain = useChain()
  const chainHeight = Number(chain.data?.height ?? 0)
  return <CatalogPage
    query={query}
    onNavigate={onNavigate}
    renderRentAction={(host) => <CatalogRentAction host={host} chainHeight={chainHeight} onNavigate={onNavigate} />}
  />
}

/**
 * The front door's live lines and resumable draft. Nothing here blocks the
 * page: an unknown count or price omits its line, and a missing wallet, draft
 * or origin simply means no resume bar.
 */
export function RentStartRoute({ walletAddress, onNavigate }: { walletAddress?: string | undefined; onNavigate?: ((path: string) => void) | undefined }) {
  const catalog = useCatalog(new URLSearchParams())
  const hosts = catalog.data?.hosts
  // `rentable` is observation freshness, not free capacity (tenant.py), so a
  // host also needs free CPU to count as one a tenant could rent from now.
  const hostsWithCapacity = hosts
    ? hosts.filter((host) => host.rentable && host.observation_state === 'fresh' && (host.available?.cpuMilli ?? 0) > 0).length
    : undefined
  const indicativeFromMicros = hosts ? lowestObservedMicrosPerHour(observedReferencePrices(hosts)) : null
  const [draftVersion, setDraftVersion] = useState(0)
  const draft = useMemo(() => {
    if (!walletAddress) return null
    const stored = readStoredDraft(walletAddress)
    const origin = readDraftOrigin(walletAddress)
    if (!stored || !origin || isUntouchedDraft(stored)) return null
    const host = origin.host ? hosts?.find((candidate) => candidate.owner === origin.host) : undefined
    return {
      name: stored.name,
      cpu: stored.cpu,
      ram: stored.ram,
      storage: stored.storage,
      hostLabel: host ? hostDisplayName(host) : null,
      resumePath: resumePathFor(origin),
    }
    // draftVersion re-reads storage after a discard.
  }, [walletAddress, hosts, draftVersion])
  return <RentStartPage
    onNavigate={onNavigate}
    hostsWithCapacity={hostsWithCapacity}
    indicativeFromMicros={indicativeFromMicros}
    draft={draft}
    onDiscardDraft={() => { discardStoredDraft(walletAddress); setDraftVersion((version) => version + 1) }}
  />
}

/**
 * Door A is gated from the host row with the provider's own capability and
 * offer answers. This keeps a host that cannot sell shared capacity from
 * sending a tenant into a configure screen that can only fail.
 */
export function CatalogRentAction({ host, chainHeight, onNavigate }: { host: CatalogHostDto; chainHeight: number; onNavigate?: ((path: string) => void) | undefined }) {
  const capability = useRentalCapability(host.owner)
  const offers = useRentalOffers(host.owner)
  const fresh = freshOffers(offers.data?.offers, chainHeight)
  const unavailable = unavailablePaths(
    capability.data,
    capability.isLoading || offers.isLoading || !chainHeight,
    capability.error ?? offers.error,
    fresh,
  )
  const reason = unavailable['host-available']
  const title = reason ?? undefined
  return <div className="catalog-rental-action">
    <Button
      size="sm"
      variant="primary"
      title={title}
      disabled={Boolean(reason)}
      onClick={(event) => { event.stopPropagation(); if (!reason) onNavigate?.(rentalPathHref(host.owner)) }}
    >
      {reason ? 'Unavailable' : 'Rent this host'}
    </Button>
    {reason ? <span className="field-help" role="status">{reason}</span> : null}
  </div>
}

/**
 * The S3 connection's instance source: the tenant's active leases that run an
 * S3 instance image, and a way to read one's endpoint. Reading the endpoint
 * needs a provider token, so it asks the wallet — only when the tenant presses
 * Look up endpoint, never on its own.
 */
function useS3Instances(connection: Connection | null | undefined, chain: ChainInfo | null | undefined): { instances: S3InstancesState; resolveEndpoint: ((candidate: S3InstanceCandidate) => Promise<string | null>) | undefined } {
  const leases = useLeases(connection?.address)
  const candidates = useMemo(() => {
    if (!connection || !leases.data) return []
    const storage = browserStorage()
    const labels = storage ? readDeploymentLabels(storage, connection.address) : {}
    return s3InstanceCandidates(leases.data.leases, (dseq) => deployedSdl(connection.address, dseq), labels)
  }, [connection, leases.data])
  const instances: S3InstancesState = !connection
    ? { status: 'unavailable', reason: 'Connect a wallet to pick one of your S3 instances.' }
    : leases.error
      ? { status: 'error', message: leases.error instanceof Error ? leases.error.message : String(leases.error), retry: () => { void leases.refetch() } }
      : leases.data
        ? { status: 'ready', instances: candidates }
        : { status: 'loading' }
  const resolveEndpoint = connection && chain ? async (candidate: S3InstanceCandidate) => {
    const found = candidates.find((item) => item.dseq === candidate.dseq)
    if (!found) return null
    const { provider, gseq, oseq } = found.lease
    const scope = { issuer: connection.address, provider, dseq: found.dseq }
    const [{ signProviderJwt }, { signArbitrary }, { providerToken, PROVIDER_TOKEN_SCOPES }, { leaseStatusModel }] = await Promise.all([import('../core/jwt'), import('../core/wallet'), import('../core/providerToken'), import('../features/provider/LeaseStatusView')])
    const token = await providerToken(scope, (expiresInMinutes) => signProviderJwt({ issuer: connection.address, provider, dseq: Number(found.dseq), services: [found.service], scopes: [...PROVIDER_TOKEN_SCOPES], expiresInMinutes }, { signArbitrary: (message) => signArbitrary(connection.wallet, chain, connection.address, message) }))
    const model = leaseStatusModel(await endpoints.providerLeaseStatus(provider, found.dseq, gseq, oseq, token))
    const service = model?.services.find((item) => item.name === found.service) ?? model?.services[0]
    return service ? s3EndpointFromUris(service.uris) : null
  } : undefined
  return { instances, resolveEndpoint }
}

export function ConfigureRoute({ walletAddress, chain, connection, onNavigate }: { walletAddress?: string | undefined; chain?: ChainInfo | null; connection?: Connection | null | undefined; onNavigate?: ((path: string) => void) | undefined }) {
  const catalog = useCatalog(new URLSearchParams())
  const s3 = useS3Instances(connection, chain)
  const params = new URLSearchParams(window.location.search)
  const hostAddress = params.get('host')
  const selectedHost = hostAddress ? catalog.data?.hosts.find((host) => host.owner === hostAddress) ?? null : null
  const rentalOffers = useRentalOffers(hostAddress ?? undefined)
  const fresh = freshOffers(rentalOffers.data?.offers, Number(chain?.height ?? 0))
  const requestedOffer = params.get('offer')
  const rawPath = params.get('path')
  const rentalPath = rawPath && (RENTAL_PATHS as readonly string[]).includes(rawPath) ? (rawPath as RentalPath) : undefined
  // A tenant-entered maximum turns even a host-started path into a network-
  // wide competitive request. Only a host-bound direct mode may carry an
  // offer into Configure; competitive sizing must keep the network defaults.
  const tenantMax = params.get('max') ?? undefined
  const requestedMode = rentalPath ? resolveMode({ path: rentalPath, priceCap: tenantMax ?? '' }) : undefined
  const capacityEnvelope = useCapacityEnvelope(requestedMode === 'RENTAL_MODE_COMPETITIVE')
  // A host's Size card offers the whole host beside the sizes a tenant picks,
  // so custom capacity still reads the host's shared-capacity offer: it is
  // what the Whole host card rents and prices. Custom capacity itself is
  // quoted against the provider and never carries this offer onward.
  const offerMode = requestedMode === 'RENTAL_MODE_DIRECT_CUSTOM' ? 'RENTAL_MODE_DIRECT_HOST_AVAILABLE' : requestedMode
  const modeOffers = offerMode && offerMode !== 'RENTAL_MODE_COMPETITIVE'
    ? offersForMode(fresh, offerMode)
    : []
  const selectedOffer = hostAddress && requestedMode && requestedMode !== 'RENTAL_MODE_COMPETITIVE'
    ? modeOffers.find((offer) => offer.host_offer_id === (requestedOffer ?? undefined)) ?? modeOffers[0] ?? null
    : null

  // The host's posted rate for a host product, read from the offer the review
  // will quote against. Undefined while no offer is known — "not read yet" is
  // not "posts no price" — and for modes that have no per-product rate.
  const postedRate = offerMode && isHostProduct(offerMode) && selectedOffer ? offerPriceForMode(selectedOffer, offerMode) : undefined

  // "Add my IP": asked for only once a port is restricted (PortAccess calls
  // request), so an unrestricted rental never sends the address anywhere.
  const [wantMyIp, setWantMyIp] = useState(false)
  const myIpQuery = useMyIp(wantMyIp)
  const requestMyIp = useCallback(() => setWantMyIp(true), [])
  const myIp = useMemo<MyIpState>(() => ({
    status: !wantMyIp ? 'idle' : myIpQuery.data ? 'ready' : myIpQuery.error ? 'error' : 'loading',
    ip: myIpQuery.data?.ip,
    request: requestMyIp,
  }), [wantMyIp, myIpQuery.data, myIpQuery.error, requestMyIp])

  // The path the chooser sent. Validated against the known set rather than
  // cast: this arrives from a URL a tenant can edit, and an unrecognised value
  // must mean "no choice recorded" — which shows the old, mode-less form —
  // rather than a mode the rest of the journey then reasons about.
  // The tenant's own maximum, if they typed one on the chooser. Distinct from
  // "the price field has a value": the guided draft's default price cap is
  // non-empty, and the price→competitive rule cannot tell a default from an
  // entry, so only an explicit `max` counts as the tenant having set one.
  return <ConfigurePage walletAddress={walletAddress} selectedHost={selectedHost} selectedOffer={selectedOffer} hostLoading={Boolean(hostAddress && (catalog.isLoading || catalog.isFetching || rentalOffers.isLoading || rentalOffers.isFetching))} rentalPath={rentalPath} tenantMax={tenantMax} capacityEnvelope={capacityEnvelope.data ?? null} capacityEnvelopeLoading={capacityEnvelope.isLoading || capacityEnvelope.isFetching} capacityEnvelopeError={capacityEnvelope.error} postedRate={postedRate} onNavigate={onNavigate} onRefreshHost={hostAddress ? () => { void catalog.refetch(); void rentalOffers.refetch() } : undefined} myIp={myIp} s3Instances={s3.instances} resolveS3Endpoint={s3.resolveEndpoint} />
}

/**
 * The rental path chooser's route.
 *
 * The provider comes from the `host` query parameter, which is how the catalog
 * has always passed it. That parameter is a REQUEST to rent that host, never a
 * binding — the binding is a signed quote, which does not exist at this point
 * in the journey. Plan §3 records the previous flow treating it as though it
 * were one, so this wrapper hands it on as a selection and nothing more.
 */
/**
 * The direct rental review route.
 *
 * Reads the same `host` and `path` parameters the chooser writes, validating
 * the path against the known set rather than casting it: it arrives from a URL
 * a tenant can edit, and an unrecognised value must mean "no choice recorded"
 * — which sends them back to the chooser — rather than a product this journey
 * then prices.
 */
/**
 * Transport for the direct-rental review screen.
 *
 * Everything that touches the network or storage lives here rather than in the
 * feature component, because `src/test/architecture.test.ts` forbids a feature
 * from importing `data/endpoints` or holding a query hook. That rule earned its
 * keep on this screen: the first draft fetched its own quote *and* generated
 * its own SDL, one layer below where the quote request was going to live, which
 * would have let the two halves of the same rental disagree about the
 * configuration being priced. Now there is exactly one `generatedSdl` string —
 * produced here, sent with the quote request, and handed down for signing.
 */
export function DirectRentalGateway({ account, chain, connection, onNavigate }: { account: Account | null; chain: ChainInfo | null; connection: Connection | null; onNavigate?: ((path: string) => void) | undefined }) {
  const signedTx = useSignedTx()
  const params = new URLSearchParams(window.location.search)
  const host = params.get('host') ?? undefined
  const raw = params.get('path')
  const path = raw && (RENTAL_PATHS as readonly string[]).includes(raw) ? (raw as RentalPath) : undefined
  const mode = path ? RENTAL_PATH_COPY[path].mode : undefined
  // Custom capacity is quoted against the provider, never a host offer: a
  // host offer never advertises it, and the provider (like the chain's
  // ValidateHostOfferID) refuses a custom quote that names one. It places the
  // rental on the first node with room.
  const customCapacity = mode === 'RENTAL_MODE_DIRECT_CUSTOM'

  const [quote, setQuote] = useState<QuoteWire | null>(null)
  const [priceCeiling, setPriceCeiling] = useState<GroupPriceCeiling | null>(null)
  const [quoteError, setQuoteError] = useState<string | null>(null)

  const height = Number(chain?.height ?? 0)

  // Read once per address, not per render: the draft is the tenant's stored
  // configuration and re-reading it mid-journey is how a quote and a signed
  // deployment come to describe different machines.
  const guided = useMemo(() => {
    let parsed: Partial<GuidedDraft> = {}
    try { parsed = JSON.parse(window.localStorage.getItem(`ddws.console.draft.${connection?.address ?? ''}`) ?? '{}') as Partial<GuidedDraft> } catch { /* normalizer defaults */ }
    return normalizeGuidedDraft(parsed)
  }, [connection?.address])
  /*
    Restore the tenant's *selection* across a page load — never a signable
    quote.

    The stored draft holds `DirectQuoteView`: quote id, provider, host offer,
    price and expiry height. It deliberately does not hold the wire quote's
    signature, nonce or group-spec hash, so a signable quote cannot be
    reconstructed from storage even if the stored one is still live. That is
    worth stating as a property rather than treating as a missing field: it
    means a price sitting in localStorage can never be walked into a wallet
    prompt. The tenant always signs a quote this page just received.

    What restoring does buy is that a tenant who reloads, or arrives on a link
    without `?host=`, keeps the host they chose instead of being sent back to
    the chooser. `loadRentalDraft` requires a height and drops an expired
    quote; until this call it had no non-test caller, so that guard was
    protecting nobody.
  */
  const [remembered, setRemembered] = useState<RentalDraft | null>(null)
  useEffect(() => {
    if (!connection || !height) return
    setRemembered(loadRentalDraft(window.localStorage, connection.address, '', height))
  }, [connection, height])

  // The URL wins when it names a host: a link the tenant just followed is a
  // more recent statement of intent than a draft from a previous visit.
  const hostOfferId = customCapacity ? undefined : params.get('offer') ?? remembered?.hostOfferId

  const offers = useRentalOffers(host)
  const capability = useRentalCapability(host)
  const liveCandidates = mode && !customCapacity ? offersForMode(freshOffers(offers.data?.offers, height), mode) : []
  /*
    A provider suppresses a host from its offer list while a quote lock is held
    on it — including the lock this tenant's own pending quote created. Without
    this, the tenant's first quote makes the host vanish and every retry fails
    with no offer to quote. So when the live list is empty and the remembered
    draft holds a still-live quote for this host and product, that offer is
    reused: the lock suppressing it is the tenant's own. The price is the one
    the provider already quoted for this product, so it cannot drift.
  */
  const rememberedQuote = remembered?.quote
  const rememberedHostOfferId = remembered?.hostOfferId
  const capabilityVersion = capability.data?.capability_version
  // Memoized so `chosenOffer`, and with it `requestQuote`, keeps its identity
  // across renders on exactly the retry path this exists for.
  const rememberedOffer = useMemo<RentalHostOfferDto | undefined>(() => {
    // A live offer always wins; the remembered one only fills an empty list.
    if (customCapacity || liveCandidates.length !== 0 || !mode || !rememberedHostOfferId || !rememberedQuote) return undefined
    // It must be this tenant's quote for this host and this product.
    if (rememberedQuote.provider !== host || rememberedQuote.rentalMode !== mode) return undefined
    if (rememberedQuote.hostOfferId !== rememberedHostOfferId) return undefined
    // Past its expiry the lock is gone, and so is any reason to reuse it.
    if (!(height > 0 && height < rememberedQuote.expiryHeight)) return undefined
    return {
      host_offer_id: rememberedHostOfferId,
      provider: rememberedQuote.provider,
      products: [mode],
      allocatable: NO_CAPACITY,
      available: NO_CAPACITY,
      price_per_block: rememberedQuote.pricePerBlock,
      product_prices: { [mode]: rememberedQuote.pricePerBlock },
      observed_height: height,
      observed_at: 0,
      expiry_height: rememberedQuote.expiryHeight,
      // The quote could only have been issued by a provider on a supported
      // contract, so the minimum supported version is the honest default.
      capability_version: capabilityVersion ?? MIN_RENTAL_CAPABILITY_VERSION,
    }
  }, [customCapacity, liveCandidates.length, mode, rememberedHostOfferId, rememberedQuote, host, height, capabilityVersion])
  const candidates = rememberedOffer ? [rememberedOffer] : liveCandidates
  // The URL/draft selection wins when it names a currently-fresh offer; this
  // is also the exact offer whose price becomes the host-product SDL ceiling.
  const chosenOffer = candidates.find((offer) => offer.host_offer_id === hostOfferId) ?? candidates[0]
  // Null for an exclusive host with no published exclusive price: that offer
  // cannot be quoted, and the base price would only be refused.
  const offerPrice = mode ? offerPriceForMode(chosenOffer, mode) : null

  const plaintext = plaintextExposure(guided)
  // A restricted port needs a host that enforces the list. Competitive rentals
  // get that from placement; a direct rental names its provider, so the
  // console checks the provider's on-chain attributes before the wallet opens.
  const needsAllowlist = requiresAllowlist(guided)
  const providerRecord = useProvider(host, needsAllowlist)
  const providerEnforcesAllowlist = !needsAllowlist ? undefined
    : providerRecord.data ? hostEnforcesAllowlist((providerRecord.data as { attributes?: unknown }).attributes)
    : providerRecord.error ? false : undefined
  const generatedSdl = useMemo(() => {
    try {
      if (mode === 'RENTAL_MODE_DIRECT_CUSTOM') {
        // A direct custom rental uses the provider-posted price stored by the
        // configure screen. Keep the editable tenant cap blank.
        return rentalDraftToSdl({ ...guided, priceCap: '' })
      }
      if (mode && mode !== 'RENTAL_MODE_COMPETITIVE') {
        // Host products are priced from the selected host offer, not from the
        // custom posted-capacity field. Carry that offer into SDL so a quote
        // for a replicated group is checked against price × count.
        const hourly = offerPrice ? usdDdtPerHour({ denom: 'uddt', amount: offerPrice }, 6) : null
        if (!hourly) return ''
        return guidedDraftToSdl({ ...guided, priceCap: hourly })
      }
      return rentalDraftToSdl(guided)
    } catch {
      return ''
    }
  }, [offerPrice, guided, mode])

  const requestQuote = useCallback(async () => {
    // The current quote and ceiling are kept until a replacement arrives or
    // the request fails for good. Clearing them first turned a failed retry
    // into a dead end: the tenant lost a still-live quote to a request that
    // was never going to succeed.
    const failTerminally = (message: string) => {
      setQuote(null)
      setPriceCeiling(null)
      setQuoteError(message)
    }
    if (!generatedSdl) {
      if (mode === 'RENTAL_MODE_DIRECT_CUSTOM') {
        failTerminally('The provider price is missing from this configuration. Return to Configure to refresh the provider rates before requesting a quote.')
        return
      }
      if (mode && mode !== 'RENTAL_MODE_COMPETITIVE') {
        if (!chosenOffer) {
          failTerminally(emptyOffersCopy(capability.data))
        } else if (!offerPrice) {
          failTerminally(mode === 'RENTAL_MODE_DIRECT_HOST_EXCLUSIVE'
            ? 'This host does not publish an exclusive price, so it cannot be rented whole. Choose its free capacity or another host.'
            : 'This host does not publish a price for this product. Choose another host.')
        } else {
          // An offer and a price exist, but the price did not convert to an
          // hourly cap: a conversion failure, not a missing price.
          failTerminally('The price this host posted could not be read. Try again, or choose another host.')
        }
        return
      }
      failTerminally('A provider-posted price is required before requesting a direct rental quote.')
      return
    }
    // A remembered host offer is used only if it is still among the fresh
    // candidates. A host that has since expired or stopped offering this
    // product must not be re-quoted just because it is in storage.
    const chosen = customCapacity ? '' : chosenOffer?.host_offer_id
    if (!height) {
      // Without a height there is no dseq to sign over, and a quote signed
      // against a made-up one would be rejected by the chain after the tenant
      // had already approved it. Refusing here is the honest failure.
      setQuoteError('Waiting for the chain to report a block height before a price can be requested.')
      return
    }
    if (!host || !mode || !connection || (!customCapacity && !chosen)) {
      failTerminally(chosen ? 'No host here can price this product right now. Try another host, or set a maximum price and request offers.' : emptyOffersCopy(capability.data))
      return
    }
    try {
      const { priceCeiling: nextPriceCeiling, ...body } = await quoteRequestFor({
        tenant: connection.address,
        hostOfferId: chosen ?? '',
        rentalMode: mode,
        generatedSdl,
        // The provider signs over this dseq. `prepareDirectRental` then reads
        // the dseq back off the returned quote rather than recomputing it, so
        // the deployment cannot be created under a different sequence than the
        // one that was priced — even if the height moved on in between.
        dseq: BigInt(height),
      })
      const dto = (await endpoints.rentalQuote(host, body)) as RentalQuoteDto & { error?: RentalErrorDto }
      if (dto.error) { failTerminally(rentalErrorCopy(dto.error)); return }
      const fresh = dto.quote as unknown as QuoteWire
      setQuoteError(null)
      setQuote(fresh)
      setPriceCeiling(nextPriceCeiling)
      if (connection && path) {
        saveRentalDraft(window.localStorage, connection.address, {
          path,
          mode,
          pricingSource: 'provider-quote',
          provider: fresh.provider,
          hostOfferId: fresh.host_offer_id,
          quote: {
            quoteId: fresh.nonce.slice(0, 12),
            provider: fresh.provider,
            hostOfferId: fresh.host_offer_id,
            rentalMode: mode,
            pricePerBlock: fresh.price.amount,
            expiryHeight: Number(fresh.expiry_height),
          },
        })
      }
    } catch (err) {
      setQuoteError(err instanceof Error ? err.message : rentalErrorCopy(undefined))
    }
  }, [host, mode, customCapacity, connection, generatedSdl, height, chosenOffer, offerPrice, capability.data, path])

  return (
    <Suspense fallback={<div className="skeleton-stack"><span className="skeleton" /><span className="skeleton" /></div>}>
      <DirectRentalPage
        account={account}
        chain={chain}
        connection={connection}
        signedTx={signedTx}
        provider={host}
        path={path}
        candidates={candidates}
        selectedOfferId={customCapacity ? undefined : chosenOffer?.host_offer_id}
        quote={quote}
        quoteError={quoteError}
        onRequestQuote={requestQuote}
        onLeased={() => { if (connection) { clearRentalDraft(window.localStorage, connection.address); discardStoredDraft(connection.address) } }}
        generatedSdl={generatedSdl}
        buildInput={guided.buildInput}
        priceCeiling={priceCeiling}
        resources={formatResources(guided)}
        size={{ cpu: guided.cpu, ram: guided.ram, storage: guided.storage }}
        label={{ name: guided.name, image: guided.image }}
        plaintext={plaintext ? { exposure: plaintext, acknowledged: unacknowledgedPlaintext(guided) === null } : undefined}
        ports={guided.ports}
        providerEnforcesAllowlist={providerEnforcesAllowlist}
        onNavigate={onNavigate}
      />
    </Suspense>
  )
}

export function RentalPathRoute({ onNavigate }: { chain?: ChainInfo | null; onNavigate?: ((path: string) => void) | undefined }) {
  useEffect(() => {
    onNavigate?.('/app/rent')
  }, [onNavigate])
  return <section className="rental-path-page">
    <PageHeader title="Opening rental options" description="The rental choices moved to the front door." />
    <p role="status">Taking you to the rental choices…</p>
    <Button variant="primary" onClick={() => onNavigate?.('/app/rent')}>Continue</Button>
  </section>
}

export function InstancesRoute({ account, onConnect, onNavigate }: { account: Account | null; onConnect?: (() => void) | undefined; onNavigate?: ((path: string) => void) | undefined }) {
  const leases = useLeases(account?.address)
  // An open deployment with no lease is invisible in a list of leases, so the
  // deployment list is read alongside it and the difference is shown. The
  // catalog names the hosts and escrow holds the balances; each of the three
  // can fail on its own without failing the list.
  const deployments = useDeployments(account?.address)
  // The catalog's largest page, so a host past the default 50 still has a name.
  const catalog = useCatalog(new URLSearchParams({ limit: '100' }))
  const escrow = useEscrow(account?.address)
  const chain = useChain()
  const address = account?.address
  const labels = useMemo(() => {
    const storage = browserStorage()
    return address && storage ? readDeploymentLabels(storage, address) : {}
  }, [address])
  const query = mapQuery<LeaseListDto, InstanceRowModel[]>(leases, (dto) => {
    const list = toLeaseList(dto)
    return buildInstanceList({
      leases: list,
      pending: pendingDeployments(deployments.data?.deployments, list.leases.map((lease) => lease.dseq)),
      deployments: deployments.data?.deployments ?? null,
      escrow: escrow.data ?? null,
      catalogHosts: catalog.data?.hosts ?? null,
      labels,
      height: parseHeight(chain.data?.height),
    })
  })
  const failed = ([['catalog', catalog], ['deployments', deployments], ['escrow', escrow]] as const)
    .filter(([, source]) => source.error && !source.data)
    .map(([name]) => name)
  const retrySources = () => {
    if (catalog.error) void catalog.refetch()
    if (deployments.error) void deployments.refetch()
    if (escrow.error) void escrow.refetch()
  }
  return <InstancesPage
    account={account}
    query={query}
    view={viewFromSearch(window.location.search)}
    chainHeight={parseHeight(chain.data?.height)}
    failedSources={failed}
    onRetrySources={retrySources}
    onConnect={onConnect}
    onNavigate={onNavigate}
  />
}

export function InstanceDetailRoute({ account, dseq, chain, connection, onNavigate }: { account: Account | null; dseq: string; chain: ChainInfo | null; connection: Connection | null; onNavigate?: ((path: string) => void) | undefined }) {
  const signedTx = useSignedTx()
  const leases = useLeases(account?.address)
  const deployment = useDeployment(account?.address, dseq)
  // The detail record's lease first — it is fetched for this dseq alone — and
  // the account-wide list second, so the page is right whichever lands first.
  const detailLease = leaseForDseq(deployment.data?.leases, dseq)
  const leaseId = leaseIdOf(detailLease ?? leaseForDseq(leases.data?.leases, dseq))
  const provider = leaseId?.provider
  // The same catalog read as the instances list, so React Query shares it;
  // it only names the host, and the page stands without it.
  const catalog = useCatalog(new URLSearchParams({ limit: '100' }))
  // The host's on-chain attributes say whether its ingress serves HTTPS
  // (`capabilities/ingress/tls`). While they load, or if the read fails, the
  // page says it does not know, never that the host lacks HTTPS.
  const providers = useProviders(Boolean(provider))
  const ingressHttps = ingressState(providers.data?.providers?.find((candidate) => candidate.owner === provider)?.attributes, { loading: providers.isLoading, failed: Boolean(providers.error) })
  const liveChain = useChain()
  const address = account?.address
  const labels = useMemo(() => {
    const storage = browserStorage()
    return address && storage ? readDeploymentLabels(storage, address) : {}
  }, [address])
  const model = buildInstanceDetail({
    dseq,
    detail: deployment.data ?? null,
    lease: detailLease,
    provider,
    catalogHosts: catalog.data?.hosts ?? null,
    labels,
    height: parseHeight(liveChain.data?.height ?? chain?.height),
  })
  const gseq = leaseId?.gseq ?? 1
  const oseq = leaseId?.oseq ?? 1
  // One approval per instance per 15 minutes: the token is held in memory and
  // covers both sending the workload and reading its status (providerToken.ts).
  const tokenScope = connection && provider ? { issuer: connection.address, provider, dseq } : null
  const signProviderToken = async () => {
    if (!connection || !chain || !provider || !account?.exists || !tokenScope) throw new Error('This account needs one signed transaction before provider access can begin.')
    const [{ signProviderJwt }, { signArbitrary }, { providerToken, PROVIDER_TOKEN_SCOPES, PROVIDER_BUILD_TOKEN_SCOPES }] = await Promise.all([import('../core/jwt'), import('../core/wallet'), import('../core/providerToken')])
    return providerToken(tokenScope, (expiresInMinutes) => signProviderJwt({ issuer: connection.address, provider, dseq: Number(dseq), services: ['app'], scopes: [...(isBuildImage(model.image) ? PROVIDER_BUILD_TOKEN_SCOPES : PROVIDER_TOKEN_SCOPES)], expiresInMinutes }, { signArbitrary: (message) => signArbitrary(connection.wallet, chain, connection.address, message) }))
  }
  /**
   * The cluster's public address, from the provider's own status, read once
   * alongside the first lease status that has a token. It never blocks or
   * fails that read: an unreachable or silent provider just leaves the ingress
   * IP "Not reported".
   *
   * Held WITH the provider it describes: this route is reused, unkeyed, when
   * the tenant moves to another instance, and one host's address shown for
   * another's service is worse than none. A reply is kept only if its
   * provider is still the one on screen, so a slow answer for the previous
   * host can neither linger nor overwrite the current one.
   */
  const [ingress, setIngress] = useState<{ provider: string; host: string | null } | null>(null)
  const ingressHost = provider && ingress?.provider === provider ? ingress.host : null
  const activeProvider = useRef(provider)
  activeProvider.current = provider
  const ingressHostRead = useRef<string | null>(null)
  const learnIngressHost = (token: string) => {
    if (!provider || ingressHostRead.current === provider) return
    const asked = provider
    ingressHostRead.current = asked
    const settle = (host: string | null) => {
      if (activeProvider.current === asked) setIngress({ provider: asked, host })
    }
    void endpoints.providerStatus(asked, token)
      .then((status) => settle(clusterPublicHost(status)))
      .catch(() => settle(null))
  }
  /** A refused token is dropped, so the next attempt asks the wallet for a new one. */
  const withProviderToken = async <T,>(call: (token: string) => Promise<T>): Promise<T> => {
    const token = await signProviderToken()
    try {
      const result = await call(token)
      learnIngressHost(token)
      return result
    } catch (cause) {
      if ((cause as { status?: unknown } | null)?.status === 401 && tokenScope) {
        const { forgetProviderToken } = await import('../core/providerToken')
        forgetProviderToken(tokenScope)
      }
      throw cause
    }
  }
  const readDraftSdl = (signedSdl: string | null) => {
    if (!connection) throw new Error('Connect the wallet that created this instance to resend its configuration.')
    // The exact text this instance was signed with, when this browser created it.
    // The draft below is only a reconstruction, and matches only if it is unchanged.
    const signed = signedSdl
    if (signed) return signed
    let parsed: Partial<GuidedDraft> = {}
    try { parsed = JSON.parse(window.localStorage.getItem(`ddws.console.draft.${connection.address}`) ?? '{}') as Partial<GuidedDraft> } catch { /* use the normalizer's defaults */ }
    const draft = normalizeGuidedDraft(parsed)
    try {
      return rentalDraftToSdl(draft)
    } catch {
      // A manifest resend does not re-price the deployment. Older persisted
      // drafts predate providerPostedPrice, so use the guided serializer's
      // valid default ceiling instead of making an existing workload unusable.
      return guidedDraftToSdl({ ...draft, priceCap: DEFAULT_GUIDED_DRAFT.priceCap })
    }
  }
  const onStartWorkload = connection && chain && provider && account?.exists ? async () => {
    const [{ readStatusWhenDeployed, sendManifestWhenLeaseSeen, workloadManifest }, { signedSdlFor }] = await Promise.all([import('../core/protocol/workload'), import('../core/instanceUpdate')])
    const rawHash = deployment.data?.deployment?.hash
    const chainHash = typeof rawHash === 'string' ? rawHash : null
    // An update that was unconfirmed when sent and has landed since is the SDL to send now.
    const signed = connection ? signedSdlFor(connection.address, dseq, chainHash) : null
    // Built and checked before the wallet is asked to sign anything.
    const manifestJson = workloadManifest(readDraftSdl(signed), chainHash)
    const buildImages = manifestBuildImages(manifestJson)
    const input = deployedBuild(connection.address, dseq)
    if (buildImages.length > 0 && (!input || buildImages.some((image) => image !== input.image))) throw new Error('The prepared Dockerfile inputs for this instance are unavailable in this browser. Return to the browser that prepared this rental to start it, or close the rental.')
    await withProviderToken((token) => sendManifestWhenLeaseSeen(() => endpoints.providerManifest(provider, dseq, manifestJson, token)))
    if (input && buildImages.includes(input.image)) {
      await withProviderToken((token) => dispatchBuild(input, provider, dseq, gseq, oseq, token, async () => {
        const { forgetProviderToken } = await import('../core/providerToken')
        if (tokenScope) forgetProviderToken(tokenScope)
        return signProviderToken()
      }))
    }
    // The host accepted the workload. From here a failure is about reading its
    // status, not about starting it, and must not be reported as a failed start.
    try {
      return await withProviderToken((token) => readStatusWhenDeployed(() => endpoints.providerLeaseStatus(provider, dseq, gseq, oseq, token)))
    } catch (cause) {
      throw new Error(`Your workload was sent to the host, but its status could not be read: ${cause instanceof Error ? cause.message : String(cause)}`)
    }
  } : undefined
  // The background status poll after a start: only ever with a token already
  // held from that start, so it can never raise a wallet prompt by itself.
  const onPollStatus = connection && chain && provider && account?.exists && tokenScope ? async () => {
    const [{ heldProviderToken }, { readStatusWhenDeployed }] = await Promise.all([import('../core/providerToken'), import('../core/protocol/workload')])
    const token = heldProviderToken(tokenScope)
    if (!token) return POLL_NO_TOKEN
    const status = await readStatusWhenDeployed(() => endpoints.providerLeaseStatus(provider, dseq, gseq, oseq, token), { attempts: 1 })
    learnIngressHost(token)
    return status
  } : undefined
  const onRefreshStatus = connection && chain && provider && account?.exists ? async () => {
    // A single read: a 404 here means the host has not set the workload up yet.
    const { readStatusWhenDeployed } = await import('../core/protocol/workload')
    return withProviderToken((token) => readStatusWhenDeployed(() => endpoints.providerLeaseStatus(provider, dseq, gseq, oseq, token), { attempts: 1 }))
  } : undefined
  /*
    Update instance (MsgUpdateDeployment): sign the new version, then send the
    host the manifest it hashes to, with the same token and lease-seen retry a
    start uses. `updateDeployment` owns the order; this only supplies the parts.
  */
  const onUpdateInstance = connection && chain && provider && account?.exists && !isBuildImage(model.image) ? async (plan: UpdatePlan) => {
    const [{ updateDeployment }, { readStatusWhenDeployed, sendManifestWhenLeaseSeen, UPDATE_MANIFEST_RETRY_STATUSES }, { offlineSigner }, { feeFor, GAS_UPDATE_DEPLOYMENT }] = await Promise.all([import('../core/instanceUpdate'), import('../core/protocol/workload'), import('../core/tx'), import('../core/fees')])
    return updateDeployment({
      broadcast: async (message) => signedTx.submit({ signer: await offlineSigner(connection.wallet, chain), address: connection.address, messages: [message], fee: feeFor(GAS_UPDATE_DEPLOYMENT), chainId: chain.chain_id }),
      // The host may not have seen the update event yet: wait that out (UPDATE_MANIFEST_RETRY_STATUSES).
      sendManifest: (manifestJson) => withProviderToken((token) => sendManifestWhenLeaseSeen(() => endpoints.providerManifest(provider, dseq, manifestJson, token), { retryStatuses: UPDATE_MANIFEST_RETRY_STATUSES })),
      remember: (sdl) => rememberDeployedSdl(connection.address, dseq, sdl),
      rememberPending: (sdl) => rememberPendingSdl(connection.address, dseq, sdl),
      clearPending: () => clearPendingSdl(connection.address, dseq),
      readStatus: () => withProviderToken((token) => readStatusWhenDeployed(() => endpoints.providerLeaseStatus(provider, dseq, gseq, oseq, token))),
    }, { owner: connection.address, dseq: BigInt(dseq), plan })
  } : undefined
  const onReadBuild = useCallback(async (): Promise<BuildReadout | null> => {
    if (!connection || !provider || (account?.address && account.address !== connection.address)) return null
    const { heldProviderToken } = await import('../core/providerToken')
    const token = heldProviderToken({ issuer: connection.address, provider, dseq })
    if (!token) return null
    const base = `/api/tenant/provider/${encodeURIComponent(provider)}/lease/${encodeURIComponent(dseq)}/${gseq}/${oseq}/build`
    const options = { headers: { Authorization: `Bearer ${token}` } }
    let status: BuildReadout['status']
    let logs: { text: string }
    try {
      status = await get<BuildReadout['status']>(`${base}/status`, options)
      logs = await get<{ text: string }>(`${base}/logs`, options)
    } catch (cause) {
      if ((cause as { status?: number }).status === 401) {
        const { forgetProviderToken } = await import('../core/providerToken')
        forgetProviderToken({ issuer: connection.address, provider, dseq })
      }
      throw cause
    }
    const application = status.phase === 'running' || status.phase === 'starting' ? await get<{ text: string }>(`${base}/application-logs`, options).catch((cause: unknown) => ({ text: cause instanceof Error ? cause.message : 'Application logs are unavailable.' })) : null
    return { status, text: logs.text, applicationText: application?.text ?? '' }
  }, [account?.address, connection?.address, provider, dseq, gseq, oseq])
  return <Suspense fallback={<div className="skeleton-stack"><span className="skeleton" /><span className="skeleton" /><span className="skeleton" /></div>}><InstanceDetailPage buildIdentity={`${account?.address ?? ""}|${connection?.address ?? ""}|${provider ?? ""}|${dseq}|${gseq}|${oseq}`} onAuthorizeBuild={async () => { await signProviderToken() }} onReadBuild={onReadBuild} account={account} dseq={dseq} chain={chain} connection={connection} signedTx={signedTx} provider={provider} lease={detailLease} model={model} deploymentQuery={deployment} onStartWorkload={onStartWorkload} onRefreshStatus={onRefreshStatus} onPollStatus={onPollStatus} ingressHost={ingressHost} ingress={ingressHttps} onNavigate={onNavigate} onUpdateInstance={onUpdateInstance} /></Suspense>
}

/**
 * The payments surface's props, or undefined when there is no wallet to fund.
 *
 * `onSignWithdrawal` is deliberately absent: signing `MsgRedeem` needs an
 * encoder this console does not have yet, and the panel refuses to open a
 * withdrawal it cannot see through rather than starting one nobody can finish.
 */
function usePaymentsProps(enabled: boolean): PaymentsPanelProps {
  const summary = usePaymentsSummary(enabled)
  const activity = usePaymentsActivity(enabled)
  const openFundingCheckout = (intent: FundingIntentDto) => {
    // Billing creates the provider checkout server-side. This keeps the
    // amount bound to the funding intent and keeps processor secrets out of
    // the browser bundle, whether the provider is fake or Stripe.
    if (!intent.checkout_url) throw new Error('That payment is not available right now.')
    window.location.assign(paymentDestination(intent.checkout_url))
  }
  return {
    summary: summary.data,
    summaryLoading: summary.isLoading,
    summaryError: summary.error,
    onRetrySummary: () => void summary.refetch(),
    activity: activity.data,
    activityLoading: activity.isLoading,
    // The key belongs to the attempt and is minted by the form that owns the
    // amount, so a retry after a timeout reaches the same intent.
    onAddFunds: (amountCents, key) =>
      endpoints.payments.createFundingIntent({
        amount_cents: amountCents,
        idempotency_key: key,
        return_path: '/app/billing',
    }),
    onOpenCheckout: openFundingCheckout,
    paymentProvider: summary.data?.payment_provider === 'stripe' ? 'stripe' : undefined,
    onPayFunding: async (funding: ActivityRowDto) => {
      const intent = await endpoints.payments.fundingIntent(funding.id)
      if (!intent.checkout_url) throw new Error('That payment is no longer available.')
      openFundingCheckout(intent)
    },
    onCancelFunding: async (funding: ActivityRowDto) => {
      await endpoints.payments.cancelFundingIntent(funding.id)
      await activity.refetch()
    },
    onStartWithdrawal: (amountCents) => endpoints.payments.createRedemption({ amount_cents: amountCents }),
    onSubmitBurnProof: (redemptionId, txHash) => endpoints.payments.submitBurnProof(redemptionId, { tx_hash: txHash }),
    onStartVerification: () => endpoints.payments.complianceSession(),
  }
}

/**
 * A joined source's state for the Billing page. A failed refresh over a cached
 * result is `stale`, not fine: the cached leases still feed a current-looking
 * runway, so the page has to say they are old and offer a retry.
 */
function billingSource(query: QueryLike<unknown>): BillingSource {
  return {
    loading: query.isLoading,
    failed: Boolean(query.error) && query.data === undefined,
    stale: Boolean(query.error) && query.data !== undefined,
    onRetry: () => void query.refetch(),
  }
}

export function BillingRoute({ account, walletConnected = Boolean(account), accountLoading = false, accountError, onRetryAccount, onConnect, onNavigate }: { account: Account | null; walletConnected?: boolean; accountLoading?: boolean; accountError?: unknown; onRetryAccount?: () => void; onConnect?: (() => void) | undefined; onNavigate?: ((path: string) => void) | undefined }) {
  const escrow = useEscrow(account?.address)
  // Deposits are joined with what spends them (leases), who hosts them (the
  // catalog) and what the tenant called them (labels). Each fails on its own:
  // escrow failing fails the deposits, the others only blank their fields.
  const leases = useLeases(account?.address)
  const catalog = useCatalog(new URLSearchParams())
  const chain = useChain()
  const address = account?.address
  const labels = useMemo(() => {
    const storage = browserStorage()
    return address && storage ? readDeploymentLabels(storage, address) : {}
  }, [address])
  const payments = usePaymentsProps(walletConnected)
  const query = mapQuery<EscrowDto, BillingViewModel>(escrow, (dto) => buildBillingModel({
    escrow: dto,
    leases: leases.data ?? null,
    catalogHosts: catalog.data?.hosts ?? null,
    labels,
    height: parseHeight(chain.data?.height),
  }))
  return <Suspense fallback={<div className="skeleton-stack" aria-label="Loading billing page"><span className="skeleton" /><span className="skeleton" /><span className="skeleton" /></div>}><BillingPage
    account={account}
    walletConnected={walletConnected}
    accountLoading={accountLoading}
    accountError={accountError}
    onRetryAccount={onRetryAccount}
    query={query}
    leases={billingSource(leases)}
    catalog={billingSource(catalog)}
    onConnect={onConnect}
    onNavigate={onNavigate}
    payments={payments}
  /></Suspense>
}

export function ReviewRoute({ account, chain, connection, onNavigate }: { account: Account | null; chain: ChainInfo | null; connection: Connection | null; onNavigate?: ((path: string) => void) | undefined }) {
  const signedTx = useSignedTx()
  return <Suspense fallback={<div className="skeleton-stack"><span className="skeleton" /><span className="skeleton" /><span className="skeleton" /></div>}><ReviewPage account={account} chain={chain} connection={connection} signedTx={signedTx} onNavigate={onNavigate} /></Suspense>
}

export function OffersRoute({ account, dseq, chain, connection, onNavigate }: { account: Account | null; dseq: string; chain: ChainInfo | null; connection: Connection | null; onNavigate?: ((path: string) => void) | undefined }) {
  const query = mapQuery<BidsDto, ReturnType<typeof toOfferList>>(useBids(account?.address, dseq), toOfferList)
  const signedTx = useSignedTx()
  // The catalog's published prices are what lets the waiting state say whether
  // the tenant's maximum is the thing keeping hosts away. They bind nobody.
  const catalog = useCatalog(new URLSearchParams())
  const observedPrices = useMemo(() => observedReferencePrices(catalog.data?.hosts), [catalog.data])
  // Once an offer is chosen the order is closed and every bid on it is `active`
  // or `lost`, so the list below would read "No offers yet" beside a button
  // that closes the deployment — now a running instance. Say what happened and
  // send the tenant to it instead.
  const leases = useLeases(account?.address)
  // The request's escrow balance: each offer says how long it lasts at that
  // offer's price (RENT-RUNTIME-PLAN W5). Nothing is billed before a lease, so
  // the balance as read is the whole deposit.
  const detail = useDeployment(account?.address, dseq)
  const escrowBalance = detail.data?.escrow?.balance
  const deposit = escrowBalance?.denom && /^\d+$/.test(escrowBalance.amount ?? '') ? escrowBalance : null
  const chosen = leaseForDseq(leases.data?.leases, dseq)
  if (chosen && chosen.lease?.state !== 'closed') {
    return <><PageHeader eyebrow="Marketplace" title="Offers for your machine" /><EmptyState title="You have already chosen a host" body="An offer for this machine was accepted, so there is nothing left to choose here. Your instance page shows the host, the rate and what happens next." action={<Button variant="primary" onClick={() => onNavigate?.(`/app/instances/${encodeURIComponent(dseq)}`)}>Open your instance</Button>} /></>
  }
  return <Suspense fallback={<div className="skeleton-stack"><span className="skeleton" /><span className="skeleton" /><span className="skeleton" /></div>}><OffersPage query={query} dseq={dseq} account={account} chain={chain} connection={connection} signedTx={signedTx} observedPrices={observedPrices} deposit={deposit} onDepositAdded={() => void detail.refetch()} onNavigate={onNavigate} /></Suspense>
}

export function SettingsRoute({ account, connection, identities, onConnectGoogle }: { account: Account | null; connection: Connection | null; identities: IdentityDto[]; onConnectGoogle: () => void }) {
  return <SettingsPage account={account} connection={connection} identities={identities} onConnectGoogle={onConnectGoogle} />
}

/**
 * The documentation surface. The reader and the guide text are both lazy: a
 * session that never opens a guide downloads neither, and one that opens a
 * guide downloads only that guide's text.
 */
export function DocsRoute({ slug, onNavigate }: { slug?: string | undefined; onNavigate?: ((path: string) => void) | undefined }) {
  const guide = findGuide(slug)
  const fallback = <div className="skeleton-stack" aria-label="Loading documentation"><span className="skeleton" /><span className="skeleton" /><span className="skeleton" /></div>
  if (slug && !guide) {
    return <><PageHeader eyebrow="Documentation" title="Guides" /><EmptyState title="That guide does not exist" body="It may have been renamed. The index lists everything published here." action={<Button onClick={() => onNavigate?.('/app/docs')}>All guides</Button>} /></>
  }
  return <Suspense fallback={fallback}>{guide ? <GuidePage guide={guide} onNavigate={onNavigate} /> : <DocsIndexPage onNavigate={onNavigate} />}</Suspense>
}

export function ActivityRoute({ onNavigate }: { onNavigate?: ((path: string) => void) | undefined }) { return <ActivityPage onNavigate={onNavigate} /> }

export function OperatorRoute({ section, role, hostId, onNavigate }: { section: OperatorSection; role: string | undefined; hostId?: string | undefined; onNavigate?: ((path: string) => void) | undefined }) {
  const allowed = ['operator', 'admin'].includes(role ?? '')
  const admin = role === 'admin'
  const queries = {
    fleet: useOperatorFleet(allowed),
    nodes: useOperatorNodes(allowed),
    providers: useOperatorProviders(allowed),
    deployments: useOperatorDeployments(allowed),
    logs: useOperatorLogs(allowed),
    pods: useOperatorPods(allowed),
    services: useOperatorServices(allowed),
    nodeports: useOperatorNodeports(allowed),
    users: useAdminUsers(admin),
  }
  const action = useOperatorAction()
  const roleChange = useAdminRoleChange()
  const power = useProviderPower()
  return <OperatorPage section={section} role={role} queries={queries} action={action} roleChange={roleChange} power={power} hostId={hostId} onNavigate={onNavigate} />
}

export function SdlRoute({ chain, connection, onDeployed }: { chain: ChainInfo | null; connection: Connection | null; onDeployed: () => void }) {
  const header = <PageHeader eyebrow="Advanced" title="SDL editor" description="Edit and sign a configuration for your next workload." />
  if (!chain) return <>{header}<p className="empty">Waiting for chain configuration…</p></>
  if (!connection) return <>{header}<p className="empty">Connect a wallet to edit and sign a configuration.</p></>
  return <>{header}<Suspense fallback={<p className="empty">Loading the editor…</p>}><SdlEditor chain={chain} connection={connection} getChain={() => endpoints.chain()} onDeployed={onDeployed} /></Suspense></>
}

export { ConfigurePage }
